import React, { useState, useRef, useCallback, useEffect } from "react";
import { useHierarchy } from "../contexts/HierarchyContext.jsx";
import { buildAgentRuntimeConfig } from "../../server/services/agent-runtime-config.js";

// Talks directly to apps/agent-runtime over the v1 wire protocol
// ({kind:"session.start"|"user.turn"|"session.end"} + assistant.delta /
// assistant.turn / tool.invoked / tool.result / chain.context / etc.).
// Avoids @configural/runtime-client because it still speaks the legacy
// {type:"configure",…} envelope and agent-runtime rejects it.

export default function ManualText({ navigate }) {
  const { subTenantId, currentSubTenant } = useHierarchy();
  const [status, setStatus] = useState("disconnected"); // disconnected | connecting | waiting | connected | error
  const [transcript, setTranscript] = useState([]);
  const [toolCalls, setToolCalls] = useState([]);
  const [activeAgent, setActiveAgent] = useState("orchestrator");
  const [taskQueue, setTaskQueue] = useState([]);
  const [inputText, setInputText] = useState("");
  const [expandedTool, setExpandedTool] = useState(null);
  const [configMode, setConfigMode] = useState("draft"); // kept for UI; agent-runtime ignores it for now

  const wsRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const sandyBufferRef = useRef("");
  const pendingToolArgsRef = useRef(new Map()); // callId → { tool, args }

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [transcript]);

  const addTranscript = useCallback((role, text, agent) => {
    if (!text) return;
    setTranscript((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.role === role && last.type === "message") {
        return [...prev.slice(0, -1), { ...last, text: last.text + text }];
      }
      return [...prev, { type: "message", role, text, agent, time: formatTime() }];
    });
  }, []);

  const disconnect = useCallback(() => {
    const ws = wsRef.current;
    wsRef.current = null;
    try {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ kind: "session.end", reason: "user-end" }));
      }
    } catch { /* ignore */ }
    try { ws?.close(1000, "user-end"); } catch { /* ignore */ }
    setStatus("disconnected");
  }, []);

  const connect = useCallback(async () => {
    setStatus("connecting");
    setTranscript([]);
    setToolCalls([]);
    setActiveAgent("orchestrator");
    setTaskQueue([]);
    sandyBufferRef.current = "";
    pendingToolArgsRef.current = new Map();

    const wsUrl = currentSubTenant?.sandyWsUrl || "ws://localhost:3010/ws";
    const sc = currentSubTenant?.sandyConfig || {};
    const tenant = sc.sandyTenantId || subTenantId || "default";
    const subTenant = sc.sandySubTenantId || currentSubTenant?.slug || tenant;

    const config = buildAgentRuntimeConfig({
      tenant,
      subTenant,
      tenantGroup: "default",
      callMode: "text",
      variables: {
        practice_name: currentSubTenant?.name || "Test Practice",
      },
    });

    let ws;
    try {
      ws = new WebSocket(wsUrl);
    } catch (err) {
      console.error("[ManualText] invalid WS URL:", err.message);
      setStatus("error");
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      setStatus("waiting");
      try {
        ws.send(JSON.stringify({ kind: "session.start", config }));
      } catch (err) {
        console.error("[ManualText] session.start send failed:", err.message);
        setStatus("error");
      }
    };

    ws.onmessage = (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); }
      catch { return; }
      handleFrame(msg);
    };

    ws.onerror = () => {
      console.error("[ManualText] WS error");
      setStatus("error");
    };

    ws.onclose = () => {
      wsRef.current = null;
      setStatus("disconnected");
    };

    function handleFrame(msg) {
      const kind = msg?.kind;
      if (!kind) return;
      switch (kind) {
        case "session.ready":
          setStatus("connected");
          return;
        case "assistant.delta":
          if (typeof msg.delta === "string") sandyBufferRef.current += msg.delta;
          return;
        case "assistant.turn": {
          const finalized = (typeof msg.text === "string" && msg.text.length > 0)
            ? msg.text
            : sandyBufferRef.current.trim();
          sandyBufferRef.current = "";
          if (finalized) addTranscript("sandy", finalized, activeAgent);
          return;
        }
        case "tool.invoked":
          pendingToolArgsRef.current.set(msg.callId, { tool: msg.tool, args: msg.args || {} });
          return;
        case "tool.result": {
          const inv = pendingToolArgsRef.current.get(msg.callId) || { tool: msg.tool, args: {} };
          pendingToolArgsRef.current.delete(msg.callId);
          const entry = {
            name: msg.tool || inv.tool,
            args: inv.args,
            result: msg.payload,
            ok: msg.ok,
            time: formatTime(),
            agent: activeAgent,
          };
          setToolCalls((prev) => [...prev, entry]);
          setTranscript((prev) => [...prev, { type: "tool_call", ...entry }]);
          if (entry.name === "end_call") setTimeout(() => disconnect(), 1500);
          if (entry.name === "complete_task") {
            const nextAgent = msg.payload?.next_agent || "orchestrator";
            const allDone = !!msg.payload?.all_tasks_done;
            setTranscript((prev) => [...prev, {
              type: "agent_switch",
              agent: nextAgent,
              task: allDone ? "All tasks completed" : msg.payload?.next_task?.description,
            }]);
          }
          return;
        }
        case "chain.context": {
          if (msg.agent) setActiveAgent(msg.agent);
          const tasks = msg.context?.tasks;
          if (Array.isArray(tasks)) setTaskQueue(tasks);
          return;
        }
        case "intents.changed":
          if (Array.isArray(msg.intents)) setTaskQueue(msg.intents);
          return;
        case "session.ended":
          setStatus("disconnected");
          return;
        case "error":
          console.error(`[ManualText] runtime error ${msg.code}: ${msg.message}`);
          setStatus("error");
          return;
        default:
          return;
      }
    }
  }, [addTranscript, subTenantId, currentSubTenant, activeAgent, disconnect]);

  useEffect(() => () => disconnect(), [disconnect]);

  const sendMessage = useCallback(() => {
    const text = inputText.trim();
    const ws = wsRef.current;
    if (!text || !ws || ws.readyState !== WebSocket.OPEN) return;

    addTranscript("patient", text, activeAgent);
    try {
      ws.send(JSON.stringify({ kind: "user.turn", text }));
    } catch (err) {
      console.error("[ManualText] user.turn send failed:", err.message);
      return;
    }
    setInputText("");
    inputRef.current?.focus();
  }, [inputText, addTranscript, activeAgent]);

  const isLive = status === "connected";
  const isOff = status === "disconnected" || status === "error";

  const AGENT_COLORS = {
    orchestrator: "#007a5a", scheduling: "#1264A3",
    "appointment-prep": "#E65100", triage: "#C62828",
    "referral-intake": "#6A1B9A",
  };

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Manual Text Chat</h1>
          <p style={s.subtitle}>Chat with Sandy via text — type as the patient</p>
        </div>
        <div style={s.headerRight}>
          {isOff && (
            <select style={s.modeSelect} value={configMode} onChange={(e) => setConfigMode(e.target.value)}>
              <option value="draft">Draft</option>
              <option value="live">Live</option>
            </select>
          )}
          {isLive && (
            <div style={{ ...s.agentBadge, background: AGENT_COLORS[activeAgent] || "#666" }}>
              {activeAgent}
            </div>
          )}
          {isOff && transcript.length > 0 && (
            <button style={s.dlBtn} onClick={() => {
              const lines = [`Text Chat Transcript`, `Date: ${new Date().toLocaleString()}`, `Config: ${configMode}`, "=".repeat(60), ""];
              for (const e of transcript) {
                if (e.type === "message") lines.push(`[${e.time}] ${e.role === "patient" ? "You" : "Sandy"}: ${e.text}`);
                else if (e.type === "tool_call") lines.push(`[${e.time}] TOOL: ${e.name}(${JSON.stringify(e.args)})`);
                else if (e.type === "agent_switch") lines.push(`--- Agent: ${e.agent} ---`);
              }
              const blob = new Blob([lines.join("\n")], { type: "text/plain" });
              const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
              a.download = `text-chat-${new Date().toISOString().slice(0, 10)}.txt`; a.click();
            }}>Download</button>
          )}
          {isOff ? (
            <button style={s.startBtn} onClick={connect}>Start Chat</button>
          ) : (
            <button style={s.endBtn} onClick={disconnect}>End Chat</button>
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div style={s.chatArea}>
        {transcript.length === 0 && isOff && (
          <div style={s.empty}>Click "Start Chat" to begin a text conversation with Sandy.</div>
        )}
        {status === "connecting" && <div style={s.statusMsg}>Connecting...</div>}
        {status === "waiting" && <div style={s.statusMsg}>Waiting for Sandy...</div>}

        {transcript.map((entry, i) => {
          if (entry.type === "message") {
            const isSandy = entry.role === "sandy";
            return (
              <div key={i} style={{ ...s.bubble, ...(isSandy ? s.bubbleSandy : s.bubblePatient) }}>
                <div style={s.bubbleRole}>{isSandy ? "Sandy" : "You"} <span style={s.bubbleTime}>{entry.time}</span></div>
                <div style={s.bubbleText}>{entry.text}</div>
              </div>
            );
          }
          if (entry.type === "agent_switch") {
            return (
              <div key={i} style={s.agentSwitch}>
                Agent: <strong>{entry.agent}</strong> {entry.task ? `— ${entry.task}` : ""}
              </div>
            );
          }
          if (entry.type === "tool_call") {
            const isExpanded = expandedTool === i;
            return (
              <div key={i} style={s.toolRow}>
                <div style={s.toolHeader} onClick={() => setExpandedTool(isExpanded ? null : i)}>
                  <span style={s.toolIcon}>⚙</span>
                  <span style={s.toolName}>{entry.name}</span>
                  <span style={s.toolTime}>{entry.time}</span>
                  <span style={s.toolChevron}>{isExpanded ? "▴" : "▾"}</span>
                </div>
                {isExpanded && (
                  <pre style={s.toolBody}>{JSON.stringify({ args: entry.args, result: entry.result }, null, 2)}</pre>
                )}
              </div>
            );
          }
          return null;
        })}
        <div ref={scrollRef} />
      </div>

      {/* Input Bar */}
      {isLive && (
        <div style={s.inputBar}>
          <input
            ref={inputRef}
            style={s.input}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            placeholder="Type your message as the patient..."
            autoFocus
          />
          <button style={s.sendBtn} onClick={sendMessage} disabled={!inputText.trim()}>
            Send
          </button>
        </div>
      )}
    </div>
  );
}

function formatTime() {
  return new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", second: "2-digit" });
}

const s = {
  page: { display: "flex", flexDirection: "column", height: "100%" },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    padding: "16px 24px", borderBottom: "1px solid #e5e5e5", flexShrink: 0,
  },
  title: { fontSize: 20, fontWeight: 600 },
  subtitle: { fontSize: 13, color: "#999", marginTop: 2 },
  headerRight: { display: "flex", alignItems: "center", gap: 8 },
  modeSelect: { padding: "6px 10px", border: "1px solid #ddd", borderRadius: 6, fontSize: 13, background: "#fff", outline: "none" },
  agentBadge: {
    padding: "4px 10px", borderRadius: 12, color: "#fff",
    fontSize: 12, fontWeight: 600,
  },
  dlBtn: {
    padding: "6px 14px", background: "#fff", color: "#555",
    border: "1px solid #e5e5e5", borderRadius: 6, fontSize: 13, fontWeight: 500,
  },
  startBtn: {
    padding: "7px 18px", background: "#0f0f0f", color: "#fff",
    border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600,
  },
  endBtn: {
    padding: "7px 18px", background: "#dc2626", color: "#fff",
    border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600,
  },
  chatArea: {
    flex: 1, overflowY: "auto", padding: "16px 24px",
    background: "#fafafa",
  },
  empty: { textAlign: "center", color: "#bbb", fontSize: 14, padding: "80px 0" },
  statusMsg: { textAlign: "center", color: "#999", fontSize: 13, padding: "20px 0" },
  // Bubbles
  bubble: { maxWidth: "75%", padding: "10px 14px", borderRadius: 12, marginBottom: 8 },
  bubbleSandy: { background: "#fff", border: "1px solid #e5e5e5", marginRight: "auto" },
  bubblePatient: { background: "#0f0f0f", color: "#fff", marginLeft: "auto" },
  bubbleRole: { fontSize: 11, fontWeight: 600, marginBottom: 4, opacity: 0.7 },
  bubbleTime: { fontWeight: 400, marginLeft: 6 },
  bubbleText: { fontSize: 14, lineHeight: 1.5 },
  // Agent switch
  agentSwitch: {
    textAlign: "center", fontSize: 11, color: "#888", padding: "6px 0",
    borderTop: "1px dashed #ddd", borderBottom: "1px dashed #ddd", margin: "8px 0",
  },
  // Tool calls
  toolRow: { marginBottom: 6 },
  toolHeader: {
    display: "flex", alignItems: "center", gap: 6, padding: "4px 8px",
    background: "#f0f0f0", borderRadius: 6, cursor: "pointer", fontSize: 12, color: "#555",
  },
  toolIcon: { fontSize: 11 },
  toolName: { fontWeight: 600, color: "#333" },
  toolTime: { marginLeft: "auto", fontSize: 10, color: "#999" },
  toolChevron: { fontSize: 10, color: "#999" },
  toolBody: {
    padding: "8px 12px", background: "#f8f8f8", borderRadius: "0 0 6px 6px",
    fontSize: 11, lineHeight: 1.4, overflow: "auto", maxHeight: 200,
    border: "1px solid #e5e5e5", borderTop: "none",
  },
  // Input bar
  inputBar: {
    display: "flex", gap: 8, padding: "12px 24px",
    borderTop: "1px solid #e5e5e5", background: "#fff", flexShrink: 0,
  },
  input: {
    flex: 1, padding: "10px 14px", border: "1px solid #e5e5e5", borderRadius: 8,
    fontSize: 14, outline: "none", color: "#0f0f0f",
  },
  sendBtn: {
    padding: "10px 20px", background: "#0f0f0f", color: "#fff",
    border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600,
  },
};
